import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { API, storage } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Bug,
  Lightbulb,
  HelpCircle,
  Loader2,
  Send,
  Paperclip,
  X,
  Download,
  Image as ImageIcon,
} from 'lucide-react';
import axios from 'axios';

const TYPE_CONFIG = {
  bug: { label: 'Bug Report', icon: Bug, color: 'text-red-600 bg-red-50 border-red-200' },
  feature: { label: 'Feature Request', icon: Lightbulb, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  other: { label: 'Other', icon: HelpCircle, color: 'text-slate-600 bg-slate-50 border-slate-200' },
};

const STATUS_STYLES = {
  open: 'bg-blue-50 text-blue-700 border-blue-200',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
  resolved: 'bg-green-50 text-green-700 border-green-200',
  closed: 'bg-slate-100 text-slate-500 border-slate-200',
};

const STATUS_LABELS = { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed' };
const SUPERADMIN_EMAILS = ['wmobil@gmail.com'];

const TicketDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [files, setFiles] = useState([]);
  const messagesEndRef = useRef(null);

  const isAdmin = user?.email && SUPERADMIN_EMAILS.includes(user.email);

  const headers = useCallback(() => {
    const token = storage.get('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, []);

  const loadTicket = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tickets/${id}`, { headers: headers() });
      setTicket(res.data);
      setMessages(res.data.messages || []);
    } catch {
      toast.error('Failed to load ticket');
      navigate('/feedback');
    } finally {
      setLoading(false);
    }
  }, [id, headers, navigate]);

  useEffect(() => { loadTicket(); }, [loadTicket]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files || []);
    const maxSize = 5 * 1024 * 1024;
    const valid = selected.filter(f => {
      if (f.size > maxSize) { toast.error(`${f.name} exceeds 5MB limit`); return false; }
      return true;
    });
    setFiles(prev => [...prev, ...valid]);
  };

  const filesToAttachments = async (fileList) => {
    return Promise.all(fileList.map(f => new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve({
        filename: f.name,
        content_type: f.type,
        data: reader.result.split(',')[1],
      });
      reader.readAsDataURL(f);
    })));
  };

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    setSending(true);
    try {
      const attachments = await filesToAttachments(files);
      await axios.post(`${API}/tickets/${id}/messages`, { message: newMessage, attachments }, { headers: headers() });
      setNewMessage('');
      setFiles([]);
      loadTicket();
    } catch {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      await axios.put(`${API}/tickets/${id}/status?status=${status}`, null, { headers: headers() });
      toast.success(`Status updated to ${STATUS_LABELS[status]}`);
      loadTicket();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const formatTime = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-64px)]">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (!ticket) return null;

  const cfg = TYPE_CONFIG[ticket.type] || TYPE_CONFIG.other;

  return (
    <div className="space-y-4" data-testid="ticket-detail-page">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(isAdmin ? '/admin' : '/feedback')} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${cfg.color}`}>
              <cfg.icon className="w-3 h-3" /> {cfg.label}
            </span>
            <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${STATUS_STYLES[ticket.status]}`}>
              {STATUS_LABELS[ticket.status]}
            </span>
          </div>
          <h1 className="text-2xl font-bold tracking-tight mt-2">{ticket.subject}</h1>
          {isAdmin && (
            <p className="text-sm text-muted-foreground mt-1">
              From: {ticket.user_name} ({ticket.user_email})
            </p>
          )}
        </div>
      </div>

      {/* Admin status controls */}
      {isAdmin && (
        <div className="flex items-center gap-2" data-testid="admin-status-controls">
          <span className="text-sm text-muted-foreground mr-1">Set status:</span>
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <Button
              key={key}
              size="sm"
              variant={ticket.status === key ? 'default' : 'outline'}
              onClick={() => handleStatusChange(key)}
              data-testid={`status-btn-${key}`}
              className="text-xs h-7"
            >
              {label}
            </Button>
          ))}
        </div>
      )}

      {/* Messages */}
      <div className="space-y-3">
        {messages.map((msg) => {
          const isMe = msg.sender_id === user?.id;
          return (
            <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`} data-testid={`message-${msg.id}`}>
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                msg.sender_role === 'admin'
                  ? 'bg-accent/10 border border-accent/20'
                  : isMe
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-slate-100'
              }`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-xs font-semibold ${isMe && msg.sender_role !== 'admin' ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                    {msg.sender_name}{msg.sender_role === 'admin' ? ' (Admin)' : ''}
                  </span>
                  <span className={`text-xs ${isMe && msg.sender_role !== 'admin' ? 'text-primary-foreground/60' : 'text-muted-foreground/60'}`}>
                    {formatTime(msg.created_at)}
                  </span>
                </div>
                <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                {msg.attachments?.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {msg.attachments.map((att, i) => {
                      const isImage = att.content_type?.startsWith('image/');
                      return (
                        <div key={i}>
                          {isImage ? (
                            <img
                              src={`data:${att.content_type};base64,${att.data}`}
                              alt={att.filename}
                              className="max-w-full max-h-64 rounded-lg mt-1"
                            />
                          ) : (
                            <a
                              href={`data:${att.content_type};base64,${att.data}`}
                              download={att.filename}
                              className="inline-flex items-center gap-1.5 text-xs underline mt-1"
                            >
                              <Download className="w-3 h-3" /> {att.filename}
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply box */}
      {ticket.status !== 'closed' && (
        <Card data-testid="reply-box">
          <CardContent className="py-3 space-y-3">
            <Textarea
              data-testid="reply-input"
              placeholder="Type your reply..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              rows={3}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSend(); }}
            />
            {files.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {files.map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-xs font-medium">
                    {f.type.startsWith('image/') ? <ImageIcon className="w-3 h-3" /> : <Paperclip className="w-3 h-3" />}
                    {f.name}
                    <button type="button" onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}>
                      <X className="w-3 h-3 text-slate-400 hover:text-slate-700" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between">
              <label className="cursor-pointer text-muted-foreground hover:text-foreground transition-colors">
                <Paperclip className="w-4 h-4" />
                <input
                  data-testid="reply-file-input"
                  type="file"
                  multiple
                  accept="image/*,.pdf,.txt,.log"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
              <Button
                data-testid="send-reply-btn"
                onClick={handleSend}
                disabled={sending || !newMessage.trim()}
                className="gap-2"
                size="sm"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Send
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TicketDetailPage;
